// Utils
import { SubscriptionProduct } from './SubscriptionProduct';

enum QuestionOneOption {
  Myself,
  Gift,
}

enum SubscriptionType {
  LatestCoffee,
  PickMyOwn,
}

/**
 * SubscriptionBuilder
 * @class
 */

class SubscriptionBuilder {
  private subscriptionBuilderContainer: HTMLElement;

  private sectionOneContainer: HTMLElement;
  private sectionTwoMyselfContainer: HTMLElement;
  private sectionTwoGiftsContainer: HTMLElement;
  private sectionThreeContainer: HTMLElement;
  private sectionFourProductContainer: HTMLElement;
  private sectionFourContainer: HTMLElement;

  private questionOne: QuestionOneOption;
  private questionTwo: SubscriptionType;
  private selectedProductId: string;

  private liveRegionElement: HTMLElement;

  private allProductElements: Array<SubscriptionProduct>;

  /**
   *
   * @param container Element containing the subscription builder
   */
  constructor(container: HTMLElement) {
    this.subscriptionBuilderContainer = container;
    this.sectionOneContainer =
      this.subscriptionBuilderContainer.querySelector('[data-section-1]');
    this.sectionTwoMyselfContainer =
      this.subscriptionBuilderContainer.querySelector(
        '[data-section-2-myself]'
      );
    this.sectionTwoGiftsContainer =
      this.subscriptionBuilderContainer.querySelector('[data-section-2-gifts]');
    this.sectionThreeContainer =
      this.subscriptionBuilderContainer.querySelector(
        '[data-section-3-coffee]'
      );
    this.sectionFourProductContainer =
      this.subscriptionBuilderContainer.querySelector(
        '[data-section-4-product]'
      );
    this.sectionFourContainer = this.subscriptionBuilderContainer.querySelector(
      '[data-section-4-coffee]'
    );

    this.liveRegionElement = this.subscriptionBuilderContainer.querySelector(
      '[data-sr-region]'
    );

    this.allProductElements = [];
    this.subscriptionBuilderContainer
      .querySelectorAll('form[data-productid')
      .forEach((el) => {
        this.allProductElements.push(
          new SubscriptionProduct(el as HTMLElement)
        );
      });

    // Check components all exist and if not hide builder
    if (
      this.sectionOneContainer == null ||
      this.sectionTwoGiftsContainer == null ||
      this.sectionTwoMyselfContainer == null ||
      this.sectionThreeContainer == null ||
      this.sectionFourProductContainer == null ||
      this.sectionFourContainer == null ||
      this.allProductElements.length == 0
    ) {
      this.subscriptionBuilderContainer.style.display = 'none';
      return;
    }

    // Section 1 Buttons
    this.sectionOneContainer
      .querySelector('[data-answer-1]')
      .addEventListener('click', (e) => {
        this.answerQuestionOne(QuestionOneOption.Myself);
      });

    this.sectionOneContainer
      .querySelector('[data-answer-2]')
      .addEventListener('click', (e) => {
        this.answerQuestionOne(QuestionOneOption.Gift);
      });

    // Section 2 Buttons
    this.sectionTwoMyselfContainer
      .querySelector('[data-answer-1]')
      .addEventListener('click', (e) => {
        this.answerQuestionTwo(SubscriptionType.LatestCoffee);
      });

    this.sectionTwoMyselfContainer
      .querySelector('[data-answer-2]')
      .addEventListener('click', (e) => {
        this.answerQuestionTwo(SubscriptionType.PickMyOwn);
      });

    this.sectionThreeContainer.querySelectorAll('[data-productid]').forEach((p) => {
      p.addEventListener('click', (e) => {
        this.selectProduct((e.target as HTMLButtonElement).dataset.productid);
      });
    });

    this.sectionTwoGiftsContainer.querySelectorAll('[data-productid]').forEach((p) => {
      p.addEventListener('click', (e) => {
        this.selectProduct((e.target as HTMLButtonElement).dataset.productid);
      });
    });

    this.showSectionOne();
  }

  /**
   *
   * @param answer Sets answer 1 and triggers UI updates
   */
  private answerQuestionOne(answer: QuestionOneOption) {
    this.questionOne = answer;
    this.questionTwo = null;
    this.showSectionTwo();
  }

  /**
   *
   * @param answer Sets answer 2 and triggers UI updates
   */
  private answerQuestionTwo(answer: SubscriptionType) {
    this.questionTwo = answer;
    if (this.questionTwo == SubscriptionType.PickMyOwn) {
      this.showSectionThree();
    } else {
      this.selectProduct('latestCoffeeProduct');
    }
  }

  /**
   *
   * @param selectedProductId Sets active product and triggers UI updates
   */
  private selectProduct(selectedProductId: string) {
    this.selectedProductId = selectedProductId;
    this.showSectionFour();
  }

  /**
   * Hides section 2 of subscription builder
   */
  private hideSectionTwo() {
    this.sectionTwoMyselfContainer.style.display = 'none';
    this.sectionTwoGiftsContainer.style.display = 'none';
  }

  /**
   * Hides section 3 of subscription builder
   */
  private hideSectionThree() {
    this.sectionThreeContainer.style.display = 'none';
  }

  /**
   * Hides section 4 of subscription builder
   */
  private hideSectionFour() {
    this.sectionFourContainer.style.display = 'none';
    this.sectionFourProductContainer.style.display = 'none';
    this.sectionFourContainer
      .querySelectorAll('[data-productid')
      .forEach((e) => {
        (e as HTMLElement).style.display = 'none';
      });
    this.sectionFourProductContainer
      .querySelectorAll('[data-productid')
      .forEach((e) => {
        (e as HTMLElement).style.display = 'none';
      });
  }

  /**
   * Shows section 1 of subscription builder
   */
  private showSectionOne() {
    this.hideSectionTwo();
    this.hideSectionThree();
    this.hideSectionFour();
    this.sectionOneContainer.style.display = 'block';
    // Update accessibility message
    this.updateLiveRegion('Section 1 - Which subscription do you want?', this.sectionOneContainer);
  }

  /**
   * Shows section 2 of subscription builder
   */
  private showSectionTwo() {
    this.hideSectionTwo();
    this.hideSectionThree();
    this.hideSectionFour();
    if (this.questionOne == QuestionOneOption.Myself) {
      this.sectionTwoMyselfContainer.style.display = 'block';
      // Update accessibility message
      this.updateLiveRegion('Section 2 - Choose your subscription', this.sectionTwoMyselfContainer);
    }
    else { 
      this.sectionTwoGiftsContainer.style.display = 'block';
      // Update accessibility message
      this.updateLiveRegion('Section 2 - Choose gift subscription', this.sectionTwoGiftsContainer);
    }
  }

  /**
   * Shows section 3 of subscription builder
   */
  private showSectionThree() {
    this.hideSectionFour();
    this.sectionThreeContainer.style.display = 'block';
    // Update accessibility message
    this.updateLiveRegion('Section 3 - Pick your coffee', this.sectionThreeContainer);
  }

  /**
   * Shows section 4 of subscription builder including selected product
   */
  private showSectionFour() {
    this.hideSectionFour();
    if (this.questionTwo == SubscriptionType.PickMyOwn) {
      this.sectionFourContainer.style.display = 'block';
      (
        this.sectionFourContainer.querySelector(
          '[data-productid="' + this.selectedProductId + '"]'
        ) as HTMLElement
      ).style.display = 'block';
      // Update accessibility message
      this.updateLiveRegion('Section 4 - Subscription checkout', this.sectionFourContainer);
    } else {
      this.sectionFourProductContainer.style.display = 'block';
      (
        this.sectionFourProductContainer.querySelector(
          '[data-productid="' + this.selectedProductId + '"]'
        ) as HTMLElement
      ).style.display = 'block';
      // Update accessibility message
      this.updateLiveRegion('Section 4 - Subscription checkout', this.sectionFourProductContainer);
    }
  }

  /**
   * Update the Aria Live Region.
   * @param message - The accessible message to be rendered.
   */
     updateLiveRegion(message: string, currentRegionElement: HTMLElement): void {
      this.liveRegionElement.textContent = message;
      // Reposition focus
      currentRegionElement.focus();
    }
}

export const subscriptionBuilderInit = () => {
  const subscriptionBuilders = document.querySelectorAll(
    '[data-subscription-builder]'
  );
  subscriptionBuilders.forEach((el) => {
    new SubscriptionBuilder(el as HTMLElement);
  });
};
